import React, { useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useDebounce } from '@/hooks/useDebounce';
import {
  useAddAnswerValue,
  useAnswerValueSuggestions,
  type AnswerValueRow,
} from '../assist/useAnswerValues';
import type { QuestionData } from '../types';

interface ScopedValueFieldProps {
  question: QuestionData;
  submissionId: string;
  placeholder?: string;
  disabled?: boolean;
}

/**
 * `submission_answers.value` is jsonb: an OV2 import, or a template whose kind
 * changed after answers existed, can leave an array/number/object/bool sitting
 * in a field that is text today. Anything non-string is treated as empty text
 * rather than coerced — `String(['a','b'])` would put `"a,b"` in front of the
 * user as if they had typed it.
 */
const asText = (value: unknown): string =>
  typeof value === 'string' ? value : '';

/**
 * Answer Assist (Feature 1): a text field that remembers. Suggestions are
 * policy-derived server-side; picking beats retyping. The "+ Add" row stays
 * visible even when the list is empty — customers repeatedly fail to discover
 * type-to-search fields, so the affordance is always on screen (locked
 * product requirement).
 *
 * Degradation: any API error renders as a plain input — the feature can only
 * ever add, never break, the form. The input itself is rendered from one
 * stable position in the tree across loading/error/success so React never
 * unmounts it; the suggestion endpoint is throttled per IP, and a single
 * corporate NAT can trip it mid-keystroke. Remounting would steal the caret.
 *
 * Keyboard: ARIA 1.2 combobox — ArrowDown/ArrowUp move `aria-activedescendant`
 * through the listbox without moving DOM focus, Enter selects, Escape closes.
 */
export const ScopedValueField: React.FC<ScopedValueFieldProps> = ({
  question,
  submissionId,
  placeholder,
  disabled,
}) => {
  const { register, setValue, watch } = useFormContext();
  const registration = register(question.slug);
  const currentValue = asText(watch(question.slug));
  const trimmedValue = currentValue.trim();

  const [open, setOpen] = useState(false);
  const [addedNote, setAddedNote] = useState<string | null>(null);
  const [addError, setAddError] = useState<string | null>(null);
  const [nearMisses, setNearMisses] = useState<AnswerValueRow[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);

  const debouncedQuery = useDebounce(currentValue, 300);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: suggestions, isError } = useAnswerValueSuggestions({
    submissionId,
    slug: question.slug,
    query: debouncedQuery,
    enabled: open && !disabled,
  });
  const addValue = useAddAnswerValue(submissionId, question.slug);

  const options = useMemo(() => suggestions ?? [], [suggestions]);
  // A failed suggestions fetch degrades the combobox to a plain text box, but
  // the <Input> below stays mounted either way.
  const degraded = isError;
  const menuOpen = open && !disabled && !degraded;
  const activeOption = activeIndex >= 0 ? options[activeIndex] : undefined;

  const listboxId = `${question.slug}-assist-listbox`;
  const optionId = (index: number) => `${question.slug}-assist-option-${index}`;
  const scopeLabel =
    question.meta?.value_scope === 'study_site' ? 'site' : 'study';

  const exactMatch = useMemo(
    () => options.some(s => s.value.toLowerCase() === trimmedValue.toLowerCase()),
    [options, trimmedValue]
  );

  const pick = (value: string) => {
    setValue(question.slug, value, { shouldDirty: true, shouldValidate: true });
    setNearMisses([]);
    setAddError(null);
    setActiveIndex(-1);
    setOpen(false);
  };

  /**
   * `force` re-posts a value the server flagged as a near miss. Without it the
   * server answers `did_you_mean` again and nothing would ever be added.
   */
  const submitValue = (force: boolean) => {
    if (!trimmedValue || addValue.isPending) return;
    setAddError(null);
    addValue.mutate(
      { value: trimmedValue, force },
      {
        onSuccess: result => {
          if (!force && result.did_you_mean?.length) {
            setAddedNote(null);
            setNearMisses(result.did_you_mean);
            return;
          }
          // `accepted` is the server's no-disclosure branch: recorded, but the
          // canonical spelling belongs to a submission this user cannot read.
          // Echo what they typed rather than inventing a value.
          const saved = result.created?.value ?? (result.accepted ? trimmedValue : null);
          if (saved !== null) {
            setNearMisses([]);
            setAddedNote(`Saved "${saved}" for this ${scopeLabel}`);
            setOpen(false);
          }
        },
        onError: error => {
          setNearMisses([]);
          setAddError(
            error instanceof Error
              ? error.message
              : "Couldn't save that value. Please try again."
          );
        },
      }
    );
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (degraded || disabled) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!open) {
          setOpen(true);
          return;
        }
        if (options.length === 0) return;
        setActiveIndex(index => (index + 1) % options.length);
        return;
      case 'ArrowUp':
        event.preventDefault();
        if (!open) {
          setOpen(true);
          return;
        }
        if (options.length === 0) return;
        setActiveIndex(index =>
          index <= 0 ? options.length - 1 : index - 1
        );
        return;
      case 'Enter':
        if (open && activeOption) {
          // Only swallow Enter when it means "take this suggestion" — an
          // otherwise-idle Enter still belongs to the form.
          event.preventDefault();
          pick(activeOption.value);
        }
        return;
      case 'Escape':
        if (open) {
          event.preventDefault();
          setActiveIndex(-1);
          setOpen(false);
        }
        return;
      default:
    }
  };

  const resultsMessage = options.length
    ? `${options.length} suggested ${options.length === 1 ? 'value' : 'values'} available`
    : 'No suggested values available';

  return (
    <div className="relative" ref={containerRef}>
      <Input
        {...registration}
        id={question.slug}
        type="text"
        role={degraded ? undefined : 'combobox'}
        aria-expanded={degraded ? undefined : menuOpen}
        aria-controls={menuOpen ? listboxId : undefined}
        aria-autocomplete={degraded ? undefined : 'list'}
        aria-haspopup={degraded ? undefined : 'listbox'}
        aria-activedescendant={
          menuOpen && activeOption ? optionId(activeIndex) : undefined
        }
        autoComplete="off"
        placeholder={
          degraded ? placeholder : (placeholder ?? 'Select or add a value…')
        }
        className="w-full"
        disabled={disabled}
        onFocus={() => {
          if (!degraded) setOpen(true);
        }}
        // Focus alone is not enough: after picking or adding a value the input
        // keeps focus with the menu closed, and clicking it again must reopen.
        onClick={() => {
          if (!degraded) setOpen(true);
        }}
        onChange={e => {
          void registration.onChange(e);
          setActiveIndex(-1);
          setAddedNote(null);
          setAddError(null);
          setNearMisses([]);
        }}
        onKeyDown={handleKeyDown}
        onBlur={e => {
          void registration.onBlur(e);
          // Delay so option mousedown wins over blur-close.
          window.setTimeout(() => {
            if (!containerRef.current?.contains(document.activeElement)) {
              setOpen(false);
              setActiveIndex(-1);
            }
          }, 150);
        }}
      />

      {!degraded && (
        <>
          <p className="sr-only" role="status" aria-live="polite">
            {menuOpen ? resultsMessage : ''}
          </p>

          {addedNote && (
            <p
              className="text-xs text-green-600 mt-1"
              role="status"
              aria-live="polite"
            >
              {addedNote}
            </p>
          )}

          {addError && (
            <p
              className="text-xs text-red-600 mt-1"
              role="alert"
              data-testid={`assist-add-error-${question.slug}`}
            >
              {addError}
            </p>
          )}

          {nearMisses.length > 0 && (
            <div
              className="mt-2 rounded-md border border-amber-400 bg-amber-50 px-3 py-2 text-sm text-amber-900"
              role="status"
              aria-live="polite"
              data-testid={`assist-did-you-mean-${question.slug}`}
            >
              <p>
                Did you mean{' '}
                {nearMisses.map((miss, index) => (
                  <React.Fragment key={miss.id}>
                    {index > 0 && ' or '}
                    <strong>{miss.value}</strong>
                  </React.Fragment>
                ))}
                ?
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {nearMisses.map(miss => (
                  <Button
                    key={miss.id}
                    type="button"
                    size="sm"
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => pick(miss.value)}
                  >
                    Use {miss.value}
                  </Button>
                ))}
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => submitValue(true)}
                  disabled={addValue.isPending}
                  data-testid={`assist-add-anyway-${question.slug}`}
                >
                  Add anyway
                </Button>
              </div>
            </div>
          )}

          {menuOpen && (
            <div
              className="absolute z-20 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg"
              data-testid={`assist-menu-${question.slug}`}
            >
              {options.length === 0 && (
                <p className="px-3 py-2 text-xs text-gray-500">
                  No values yet — anything you add helps the next submission.
                </p>
              )}
              <ul
                className="max-h-56 overflow-y-auto"
                role="listbox"
                id={listboxId}
                aria-label={`Suggested values for ${question.prompt}`}
              >
                {options.map((s, index) => (
                  <li
                    key={s.id}
                    id={optionId(index)}
                    role="option"
                    aria-selected={index === activeIndex}
                    className={`flex w-full cursor-pointer items-center justify-between px-3 py-2 text-sm hover:bg-gray-50 ${
                      index === activeIndex ? 'bg-gray-100' : ''
                    }`}
                    onMouseDown={e => e.preventDefault()}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => pick(s.value)}
                  >
                    <span>{s.value}</span>
                    <span className="text-xs text-gray-400 font-mono">
                      used {s.times_used}×
                    </span>
                  </li>
                ))}
              </ul>
              {/* + Add stays visible even with an empty list (locked requirement). */}
              {!exactMatch && (
                <button
                  type="button"
                  className="flex w-full items-center gap-2 border-t border-gray-100 px-3 py-2 text-sm font-medium text-teal-700 hover:bg-teal-50 disabled:opacity-50"
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => submitValue(false)}
                  disabled={addValue.isPending || !trimmedValue}
                  data-testid={`assist-add-${question.slug}`}
                >
                  <Plus className="h-4 w-4" />
                  {trimmedValue
                    ? `Add "${trimmedValue}"`
                    : 'Add a value — just start typing'}
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};
